// admin/src/pages/GatewayMonitoring/components/EnrollmentModal.js
'use strict';

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import KeyIcon from '@mui/icons-material/Key';
import BlockIcon from '@mui/icons-material/Block';
import {
  createEnrollmentToken,
  listEnrollmentTokens,
  revokeEnrollmentToken,
  extractGatewayErrorMessage,
} from '../gatewayApi';

export default function EnrollmentModal({
  open = false,
  onClose,
  mode = 'light',
  canDelete = false,
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [expiresInHours, setExpiresInHours] = useState(24);
  const [generating, setGenerating] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [error, setError] = useState(null);

  // Pending tokens list state
  const [tokensList, setTokensList] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [revokingTokenId, setRevokingTokenId] = useState(null);

  useEffect(() => {
    if (open) {
      loadTokens();
    } else {
      // Clear all sensitive token state when modal is closed
      setTokenData(null);
      setSavedConfirmed(false);
      setCopied(false);
      setError(null);
    }
  }, [open]);

  const loadTokens = async () => {
    setLoadingTokens(true);
    try {
      const res = await listEnrollmentTokens();
      if (res && res.data) {
        setTokensList(res.data);
      }
    } catch (_) {}
    finally {
      setLoadingTokens(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await createEnrollmentToken({ expiresInHours });
      if (res && res.data && res.data.token) {
        setTokenData(res.data);
        setSavedConfirmed(false);
        loadTokens();
      } else {
        setError('Failed to generate enrollment token.');
      }
    } catch (err) {
      setError(extractGatewayErrorMessage(err));
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (tokenData?.token) {
      navigator.clipboard.writeText(tokenData.token).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      });
    }
  };

  const handleRevokeToken = async (tokenId) => {
    setRevokingTokenId(tokenId);
    try {
      await revokeEnrollmentToken(tokenId);
      await loadTokens();
    } catch (err) {
      setError(extractGatewayErrorMessage(err));
    } finally {
      setRevokingTokenId(null);
    }
  };

  const handleClose = () => {
    // If a one-time token was generated, require confirmation checkbox
    if (tokenData && !savedConfirmed) {
      return;
    }
    // Strictly clear all token memory before closing
    setTokenData(null);
    setSavedConfirmed(false);
    setCopied(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      data-testid="enrollment-modal"
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        Gateway Enrollment
      </DialogTitle>

      <Tabs
        value={activeTab}
        onChange={(_, val) => setActiveTab(val)}
        sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Generate Enrollment Token" />
        <Tab label={`Pending Tokens (${tokensList.filter((t) => !t.usedAt && !t.revokedAt).length})`} />
      </Tabs>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {activeTab === 0 && (
          <Box>
            {!tokenData ? (
              <Stack spacing={3}>
                <Typography variant="body2" color="text.secondary">
                  Issue a single-use enrollment token to register a new AD-Agent gateway instance. The token allows the gateway to provision a durable authentication credential during its first check-in.
                </Typography>

                <FormControl fullWidth size="small">
                  <InputLabel id="token-expiry-label">Token Expiration Window</InputLabel>
                  <Select
                    labelId="token-expiry-label"
                    value={expiresInHours}
                    label="Token Expiration Window"
                    onChange={(e) => setExpiresInHours(e.target.value)}
                  >
                    <MenuItem value={1}>1 Hour</MenuItem>
                    <MenuItem value={12}>12 Hours</MenuItem>
                    <MenuItem value={24}>24 Hours (Recommended)</MenuItem>
                    <MenuItem value={48}>48 Hours</MenuItem>
                    <MenuItem value={168}>7 Days (Maximum)</MenuItem>
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Button
                    variant="contained"
                    data-testid="generate-token-btn"
                    startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <KeyIcon />}
                    onClick={handleGenerate}
                    disabled={generating}
                  >
                    Generate Token
                  </Button>
                </Box>
              </Stack>
            ) : (
              <Stack spacing={2.5} data-testid="generated-token-section">
                <Alert severity="warning" sx={{ fontWeight: 600 }}>
                  Save this enrollment token now. It is shown only once and cannot be retrieved again after closing this dialog!
                </Alert>

                <Box
                  data-testid="token-display-box"
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    bgcolor: mode === 'dark' ? '#111827' : '#F3F4F6',
                    border: '1px solid',
                    borderColor: mode === 'dark' ? '#374151' : '#E5E7EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    variant="body2"
                    data-testid="plaintext-enroll-token"
                    sx={{
                      fontFamily: 'Consolas, monospace',
                      wordBreak: 'break-all',
                      fontWeight: 600,
                      userSelect: 'all',
                    }}
                  >
                    {tokenData.token}
                  </Typography>

                  <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                    <IconButton onClick={handleCopy} color={copied ? 'success' : 'primary'} sx={{ ml: 1 }}>
                      {copied ? <CheckIcon /> : <ContentCopyIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>

                <Typography variant="caption" color="text.secondary">
                  Expires: <strong>{new Date(tokenData.expiresAt).toLocaleString()}</strong> (Token ID: {tokenData.tokenId})
                </Typography>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Gateway Installation Instructions:
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      bgcolor: mode === 'dark' ? '#1F2937' : '#F9FAFB',
                      border: '1px solid',
                      borderColor: 'divider',
                      fontSize: '0.82rem',
                      fontFamily: 'Consolas, monospace',
                      overflowX: 'auto',
                    }}
                  >
{`# 1. On your Active Directory gateway host, save the token:
echo "${tokenData.token}" > /etc/poc-gateway/enrollment.token
chmod 0600 /etc/poc-gateway/enrollment.token

# 2. Configure gateway.env:
POC_CLOUD_REPORTING_ENABLED=true
POC_CLOUD_URL=${window.location.origin}
POC_ENROLL_TOKEN_FILE=/etc/poc-gateway/enrollment.token

# 3. Start or restart the gateway service:`}
                  </Box>
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={savedConfirmed}
                      data-testid="token-saved-checkbox"
                      onChange={(e) => setSavedConfirmed(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      I have saved this token securely. I understand it will not be shown again.
                    </Typography>
                  }
                />
              </Stack>
            )}
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              List of generated enrollment tokens. Once a token is claimed by a gateway, it is marked as used.
            </Typography>

            {loadingTokens ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : tokensList.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                No active or historical enrollment tokens found.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Token ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Expires</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      {canDelete && <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tokensList.map((tok) => {
                      const isUsed = Boolean(tok.usedAt);
                      const isRevoked = Boolean(tok.revokedAt);
                      const isExpired = new Date(tok.expiresAt).getTime() < Date.now();

                      let statusChip = <Chip size="small" label="Pending" color="primary" variant="outlined" />;
                      if (isUsed) statusChip = <Chip size="small" label="Used" color="success" />;
                      else if (isRevoked) statusChip = <Chip size="small" label="Revoked" />;
                      else if (isExpired) statusChip = <Chip size="small" label="Expired" color="error" variant="outlined" />;

                      return (
                        <TableRow key={tok.tokenId}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{tok.tokenId}</TableCell>
                          <TableCell>{new Date(tok.createdAt).toLocaleDateString()}</TableCell>
                          <TableCell>{new Date(tok.expiresAt).toLocaleDateString()}</TableCell>
                          <TableCell>{statusChip}</TableCell>
                          {canDelete && (
                            <TableCell align="right">
                              {!isUsed && !isRevoked && !isExpired && (
                                <Tooltip title="Revoke this token">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={revokingTokenId === tok.tokenId}
                                    onClick={() => handleRevokeToken(tok.tokenId)}
                                  >
                                    <BlockIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={handleClose}
          variant={tokenData ? 'contained' : 'outlined'}
          disabled={Boolean(tokenData && !savedConfirmed)}
          data-testid="close-enrollment-modal-btn"
        >
          {tokenData ? 'Done' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
